import {
  CountryConfigEntry,
  FieldDefinitionEntry,
  FieldsConfig,
  FieldValidationRule,
  IBankAccountCredentials,
} from '@commenda/types';
import currencyMap from './country-currency.json';

export interface CountryOption {
  value: string;
  label: string;
}

// ─── Base layer — derived from config ────────────────────────────────────────

/**
 * Returns the set of base layer field keys for this config.
 * Base layer fields are those with `required_globally: true` in field_definitions.
 * They are always shown (Layer 1) and must never appear in the dynamic Layer 2 list.
 */
export function getBaseLayerFields(config: FieldsConfig): Set<string> {
  return new Set(
    Object.entries(config.field_definitions)
      .filter(([, def]) => def.required_globally)
      .map(([key]) => key)
  );
}

// ─── Currency list (static — from country-currency.json) ─────────────────────

/** All currencies derived from country-currency.json, deduplicated and sorted by code. */
export const ALL_CURRENCIES: CountryOption[] = Array.from(
  new Set(Object.values(currencyMap as Record<string, string>))
)
  .map((code) => ({ value: code, label: code }))
  .sort((a, b) => a.value.localeCompare(b.value));

/**
 * Returns the default ISO 4217 currency code for a country.
 * Source: country-currency.json (static).
 */
export function getDefaultCurrency(code: string): string {
  return (currencyMap as Record<string, string>)[code] ?? '';
}

// ─── Country list ─────────────────────────────────────────────────────────────

/** All countries in the config, sorted alphabetically by name. */
export function getAllCountries(config: FieldsConfig): CountryOption[] {
  return Object.entries(config.countries)
    .map(([code, c]) => ({ value: code, label: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ─── Country helpers ──────────────────────────────────────────────────────────

/** Returns the full country entry, or null if not in the config. */
export function getCountryData(
  config: FieldsConfig,
  code: string
): CountryConfigEntry | null {
  return config.countries[code] ?? null;
}

/**
 * Returns the Layer 2 required fields for a country.
 * Excludes base layer fields (those with required_globally: true).
 */
export function getRequiredFields(
  config: FieldsConfig,
  code: string
): string[] {
  const baseLayer = getBaseLayerFields(config);
  return (config.countries[code]?.required_fields ?? []).filter(
    (f) => !baseLayer.has(f)
  );
}

/**
 * Returns the Layer 2 optional fields for a country.
 * Excludes base layer fields.
 */
export function getOptionalFields(
  config: FieldsConfig,
  code: string
): string[] {
  const baseLayer = getBaseLayerFields(config);
  return (config.countries[code]?.optional_fields ?? []).filter(
    (f) => !baseLayer.has(f)
  );
}

// ─── Field definition helpers ─────────────────────────────────────────────────

/** Returns the field definition for a given key, or null if not found. */
export function getFieldDef(
  config: FieldsConfig,
  key: string
): FieldDefinitionEntry | null {
  return config.field_definitions[key] ?? null;
}

/** Returns the description for a field key, or undefined if not defined. */
export function getFieldDescription(
  config: FieldsConfig,
  key: string
): string | undefined {
  return config.field_definitions[key]?.description;
}

/**
 * Returns the effective validation rule for a field within a country context.
 * Country-level field_overrides take precedence over the global field_definition.
 */
export function getValidationRule(
  config: FieldsConfig,
  countryCode: string,
  fieldKey: string
): FieldValidationRule | null {
  const override =
    config.countries[countryCode]?.field_overrides?.[fieldKey]?.validation;
  if (override) return override;
  return config.field_definitions[fieldKey]?.validation ?? null;
}

/**
 * Returns the effective label for a field within a country context.
 * Country-level field_overrides (e.g. Nigeria overriding accountNumber → "NUBAN") take precedence.
 */
export function getFieldLabelForCountry(
  config: FieldsConfig,
  countryCode: string,
  fieldKey: string
): string {
  const overrideLabel =
    config.countries[countryCode]?.field_overrides?.[fieldKey]?.label;
  if (overrideLabel) return overrideLabel;

  const defLabel = config.field_definitions[fieldKey]?.label;
  return defLabel ?? '';
}

/**
 * Full validation pass for all base + required Layer 2 fields.
 * Call on form submit. Returns an error map; empty object means valid.
 */
export function validateBankAccountFields(
  config: FieldsConfig,
  base: {
    accountHolderName: string;
    country: string;
    bankName: string;
    currency: string;
  },
  credentials: IBankAccountCredentials
): Record<string, string> {
  const errs: Record<string, string> = {};

  if (!base.accountHolderName?.trim())
    errs.accountHolderName = 'Enter the account holder name.';

  if (!base.country)
    errs.country = 'Select the country where your bank account is held.';

  if (!base.bankName?.trim()) errs.bankName = 'Enter the bank name.';

  if (!base.currency?.trim()) errs.currency = 'Select the account currency.';

  if (base.country) {
    const countryName =
      getCountryData(config, base.country)?.name ?? base.country;
    getRequiredFields(config, base.country).forEach((fieldKey) => {
      const val = credentials[fieldKey]?.trim();
      const label = getFieldLabelForCountry(config, base.country, fieldKey);
      const rule = getValidationRule(config, base.country, fieldKey);

      if (!val) {
        errs[fieldKey] = `Enter the ${label} for your ${countryName} account.`;
        return;
      }
      if (rule?.pattern && !new RegExp(rule.pattern).test(val))
        errs[fieldKey] = `${label} doesn't match the expected format.`;
      else if (rule?.length && val.length !== rule.length)
        errs[fieldKey] = `${label} must be exactly ${rule.length} characters.`;
      else if (rule?.min_length && val.length < rule.min_length)
        errs[fieldKey] =
          `${label} must be at least ${rule.min_length} characters.`;
    });
  }

  return errs;
}
